import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { FieldInputApi } from "../shared/types";
import { useFieldSetContext } from "../contexts/field-set-context";
import { focusElement } from "../shared/utils";

export interface useFieldInputProps {
    name: string,
    focus?: (select?: boolean) => boolean
}

export default function useFieldInput({
    name,
    focus
}: useFieldInputProps): FieldInputApi {
    const fieldSet = useFieldSetContext();
    const ref = useRef<HTMLElement>(null!);

    const id = name + "-" + useId();

    const handleFocus = useCallback((select?: boolean) => {
        if (focus) return focus(select);
        return focusElement(ref.current, select);
    }, [focus]);

    useEffect(() => {
        fieldSet.register({
            id,
            name,
            element: ref.current,
            type: "fieldInput",
            focus: handleFocus,
        });

        return () => fieldSet.unregister(id);
    }, [id, name, handleFocus]);

    return useMemo(() => ({
        id,
        name,
        ref,
        fieldSet,
    }), [id, name, fieldSet])
}